/* eslint-disable react/destructuring-assignment */
import { useEffect, useState } from 'react';
import track from 'utils/amplitudeWrapper';
import {
  TableStyled,
  TrStyled,
  RankText,
  VarietyText,
  TrExpandableRow,
  TableContainer,
  ExpandedTdStyled,
  ValueRowWrapper,
  NumberText,
  CommonText,
  DescriptionText,
  SkuContainer,
} from './DensityDemandTableMobile.styles';
import { useTranslation } from 'react-i18next';
import { isBrazil } from 'utils/countryCode';
import { IconChevronRightStyled, TdMainRow } from '../DensityDemandTable/styles';
import { SeedDensityField } from '../SeedDensityAdvice';
import { RankedSeed } from 'base/types/RecommendationCropwise';
import { convertToDecimal, numberFormat, numberToEuropeanFormat } from 'utils/numberFormat';
import FieldCeil from '../FieldCeilComponent';
import { useAppState } from 'context/AppState';
import { CropConstants } from 'utils/constants/Crop';
import { convertSeedsToBagsForProduct } from 'utils/convertSeedsToBagsForProduct';
import { BrazilCountryConstant } from 'utils/constants/CountryData';

type TSeedDemand = {
  rank: number | string;
  variety: string;
  seeds: string | number;
  plantingArea: string | number;
  numberOfBags: string | number;
};

type SkuDataItem = {
  commercial_name: string;
  sku_description: string;
};

interface IProps {
  dataSource: SeedDensityField[];
  isNonDDG: boolean;
  countryCode: string;
  updatedDataSource: SeedDensityField[];
}

export type TData = {
  fieldName: string;
  fieldCoordinates: number[][][];
  seedDemand: TSeedDemand[];
  area: string | number;
  expanded?: boolean;
};

const MainRow = ({
  data,
  handleExpandableRowClick,
}: {
  data: SeedDensityField;
  isNonDDG?: boolean;
  handleExpandableRowClick: (rowData: SeedDensityField) => void;
}) => {
  return (
    <TrStyled>
      <td>
        <FieldCeil {...data} />
      </td>
      <TdMainRow data-testid="seedDensityTableChevronIconMobile" hasPaddingRight={true}>
        <IconChevronRightStyled
          isOpened={data.expanded}
          onClick={() => handleExpandableRowClick(data)}
        />
      </TdMainRow>
    </TrStyled>
  );
};

const ExpandableRow = ({
  data,
  isNonDDG,
  configRecommendation,
  field,
  countryCode,
  getSkuDescription,
  cropId,
  dataSourceLength,
}: {
  data: RankedSeed;
  isNonDDG: boolean;
  configRecommendation: any;
  countryCode: string;
  field: string;
  getSkuDescription: (varietyName: string) => string;
  cropId: number;
  dataSourceLength: number;
}) => {
  const [t] = useTranslation();
  const {
    apiData: { productCatalog },
  } = useAppState();
  const getExpandableRowValueForNonDDG = (key: string, data: RankedSeed) => {
    const seedName = data.Seed?.SeedName;

    const getSeedRate = () => {
      if (data && (data.Seed || data.SeedName)) {
        const calculatedSeedRates = configRecommendation
          .find((item: any) => item.FieldId === field)
          ?.CalculatedSeedRate?.reduce((acc: any, item: any) => {
            return { ...acc, ...item };
          }, {});

        return calculatedSeedRates?.[data?.Seed?.SeedName ?? data.SeedName];
      }
      return 0;
    };

    const plantingArea = (key: string) => {
      return countryCode === CropConstants.BULGARIA_CODE
        ? Number(((data[key as unknown as keyof RankedSeed] as number) * 10)?.toFixed(2))
        : Number((data[key as unknown as keyof RankedSeed] as number)?.toFixed(2));
    };
    if (key === 'sku') {
      return '';
    } else if (key === 'numberOfBags') {
      const bagsValue = Number(data?.numberOfBags);
      const bags = Math.ceil(bagsValue);
      if (!bags && data.Seed) {
        const product = productCatalog.find(
          (item: any) => item.productName === seedName || item.commercialName === seedName
        );
        return sanitizeNumber(
          convertSeedsToBagsForProduct(
            getSeedRate(),
            plantingArea('plantingArea'),
            product,
            countryCode
          ),
          Math.ceil
        );
      }
      return sanitizeNumber(bags);
    } else if (key === 'seeds') {
      const seedsValue = data[key as unknown as keyof RankedSeed] as number;
      return sanitizeNumber(seedsValue, (val) => {
        return countryCode === CropConstants.BULGARIA_CODE
          ? convertToDecimal(val)
          : numberFormat(val) || '';
      });
    } else if (key === 'plantingArea') {
      return sanitizeNumber(plantingArea(key));
    } else if (key === 'plants') {
      return sanitizeNumber(Number(data?.plants));
    } else if (typeof data[key as keyof RankedSeed] === 'number') {
      return sanitizeNumber(
        data[key as keyof RankedSeed] as number,
        (val) => numberToEuropeanFormat(val) || ''
      );
    } else {
      return (data[key as keyof RankedSeed] as string) || '';
    }
  };
  const getExpandableRowValueForDDG = (key: string, value: number) => {
    if (countryCode === CropConstants.BULGARIA_CODE) {
      if (key === 'seeds') {
        return sanitizeNumber(value, convertToDecimal);
      } else if (key === 'plantingArea') {
        return sanitizeNumber(value, (val) => (val * 10).toFixed(2));
      }
    }
    return sanitizeNumber(value);
  };

  const getseedRateTitle = () => {
    if (countryCode === CropConstants.SOUTH_AFRICA_CODE) {
      return <CommonText>{t('kg/ha')}</CommonText>;
    } else if (cropId === CropConstants.BARLEY_CROP_ID) {
      return <CommonText>{t('seeds/m2')}</CommonText>;
    } else if (countryCode === CropConstants.BULGARIA_CODE) {
      return <CommonText>{t('kg/decare')}</CommonText>;
    } else if (isBrazil(countryCode)) {
      // Brazil reports established plants per hectare, not seeds sown.
      return <CommonText>{t('Plants/ha')}</CommonText>;
    }

    return <CommonText>{t('seeds/ha')}</CommonText>;
  };

  const getAreaTitle = () => {
    if (countryCode === CropConstants.BULGARIA_CODE) {
      return <CommonText>{t('Area(decare)')}</CommonText>;
    }
    return <CommonText>{t('Area (ha)')}</CommonText>;
  };

  const isBagSizeVisible = countryCode === CropConstants.SOUTH_AFRICA_CODE;
  const isSkuVisible = isNonDDG && countryCode !== BrazilCountryConstant.id;

  return (
    <>
      <TrExpandableRow>
        <ExpandedTdStyled colSpan={2}>
          {!(isNonDDG && dataSourceLength > 1) && <RankText>{data.rank}</RankText>}
          <VarietyText>{data.variety}</VarietyText>
        </ExpandedTdStyled>
      </TrExpandableRow>
      <TrExpandableRow>
        <ExpandedTdStyled colSpan={2}>
          <ValueRowWrapper>
            {getseedRateTitle()}
            <NumberText>
              {isNonDDG
                ? getExpandableRowValueForNonDDG('seeds', data)
                : getExpandableRowValueForDDG('seeds', data.seeds as number)}
            </NumberText>
          </ValueRowWrapper>
        </ExpandedTdStyled>
      </TrExpandableRow>
      <TrExpandableRow>
        <ExpandedTdStyled colSpan={2}>
          <ValueRowWrapper>
            {getAreaTitle()}
            <NumberText>
              {isNonDDG
                ? getExpandableRowValueForNonDDG('plantingArea', data)
                : getExpandableRowValueForDDG('plantingArea', data?.plantingArea as number)}
            </NumberText>
          </ValueRowWrapper>
        </ExpandedTdStyled>
      </TrExpandableRow>
      <TrExpandableRow>
        <ExpandedTdStyled colSpan={2} showBottomBorder={!isBagSizeVisible && !isSkuVisible}>
          <ValueRowWrapper>
            <CommonText>{t('Bags')}</CommonText>
            <NumberText>
              {isNonDDG
                ? getExpandableRowValueForNonDDG('numberOfBags', data)
                : Math.ceil(Number(data?.numberOfBags))}
            </NumberText>
          </ValueRowWrapper>
        </ExpandedTdStyled>
      </TrExpandableRow>
      {isBagSizeVisible && (
        <TrExpandableRow>
          <ExpandedTdStyled colSpan={2} showBottomBorder={!isSkuVisible}>
            <ValueRowWrapper>
              <CommonText>{t('Bag size')}</CommonText>
              <NumberText>{numberToEuropeanFormat(data.bagSize as number)}</NumberText>
            </ValueRowWrapper>
          </ExpandedTdStyled>
        </TrExpandableRow>
      )}
      {isSkuVisible && (
        <SkuContainer>
          <ExpandedTdStyled colSpan={2}>
            <CommonText>{t('SKU')}</CommonText>
          </ExpandedTdStyled>
          <ExpandedTdStyled colSpan={2} showBottomBorder>
            <DescriptionText className="description">
              {getSkuDescription(data.variety) ?? 'N/A'}
            </DescriptionText>
          </ExpandedTdStyled>
        </SkuContainer>
      )}
    </>
  );
};

const DensityDemandTableMobile = ({
  dataSource,
  isNonDDG,
  countryCode,
  updatedDataSource,
}: IProps) => {
  const [dataToDisplay, setDataToDisplay] = useState<SeedDensityField[]>([]);
  const [skuData, setSkuData] = useState<SkuDataItem[]>([]);
  const {
    apiData: { recommendationCropwise, productCatalog },
  } = useAppState();

  const configRecommendation =
    recommendationCropwise?.recommendations[0].multi_field.config.recommendation;
  const cropId = recommendationCropwise?.recommendations[0].crop_id ?? 0;

  const getSKUForNonDDGSolutions = (productName: string) => {
    const recommendedProduct = productCatalog?.find((item) => item.commercialName === productName);
    const Sku = recommendedProduct?.sku?.find((sku) => sku.available && sku?.defaultSku);
    return Sku?.description ?? 'N/A';
  };

  const getSkuDescription = (varietyName: string) => {
    const matchedSku = skuData.find(
      (sku) => sku.commercial_name?.toLowerCase() === varietyName?.toLowerCase()
    );
    return matchedSku ? matchedSku.sku_description : getSKUForNonDDGSolutions(varietyName);
  };

  useEffect(() => {
    setDataToDisplay(updatedDataSource);
  }, [dataSource, updatedDataSource]);

  useEffect(() => {
    if (recommendationCropwise?.recommendations?.length) {
      const skuDataFetch = recommendationCropwise.recommendations[0].products.map((product) => ({
        commercial_name: product.commercial_name,
        sku_description:
          (product.sku?.description || getSKUForNonDDGSolutions(product.commercial_name)) ?? '',
      }));
      setSkuData(skuDataFetch);
    }
  }, [recommendationCropwise]);

  const handleExpandableRowClick = (rowData: SeedDensityField) => {
    const dataToDisplayCopy = [...dataToDisplay].map((row) => {
      if (row.name === rowData.name) {
        return {
          ...row,
          expanded: !row.expanded,
        };
      }
      return row;
    });
    setDataToDisplay(dataToDisplayCopy);
  };

  const renderExpandableRow = (field: SeedDensityField) => {
    let seedsDemand = [...field.seedDemand];
    if (!isNonDDG) {
      seedsDemand = seedsDemand.filter((seedDemand) => {
        const rank =
          typeof seedDemand.rank === 'string' ? parseInt(seedDemand.rank) : seedDemand.rank;
        return rank < 4;
      });
    } // We only need the first 3 ranks
    return (
      <>
        <MainRow
          data={field}
          isNonDDG={isNonDDG}
          handleExpandableRowClick={handleExpandableRowClick}
        />
        {field.expanded &&
          seedsDemand.map((seed) => (
            <ExpandableRow
              key={seed.rank}
              data={seed}
              isNonDDG={isNonDDG}
              countryCode={countryCode}
              field={field.FieldId || ''}
              configRecommendation={configRecommendation}
              getSkuDescription={getSkuDescription}
              cropId={cropId}
              dataSourceLength={dataSource.length}
            />
          ))}
      </>
    );
  };

  return (
    <TableContainer>
      <TableStyled data-testid="seedDensityAdviceTableMobile">
        <tbody>
          {dataToDisplay.map((densityDemandInfo) => renderExpandableRow(densityDemandInfo))}
        </tbody>
      </TableStyled>
    </TableContainer>
  );
};

export default DensityDemandTableMobile;

// Helper function to handle NaN values consistently
const sanitizeNumber = (
  value: number,
  formatter?: (val: number) => string | number
): string | number => {
  if (isNaN(value)) {
    return '';
  }
  return formatter ? formatter(value) : value;
};
